<template>
  <Container>
    <template v-slot:header>Queues</template>

    <template v-slot:content>
      <div class="w-full flex flex-col gap-6">
        <p v-if="loading" class="text-sm text-gray-500">Loading…</p>
        <div
          v-else-if="loadError"
          class="bg-white shadow rounded-lg p-6 w-full text-left"
        >
          <p class="text-sm text-gray-700">
            Couldn't load the queues. Their status is unknown.
          </p>
          <button type="button" class="button-submit mt-4" @click="reload">
            Retry
          </button>
        </div>
        <template v-else>
          <QueuesTable
            title="Consumers"
            description="Queues the Alfred App consumes and owns. Pausing one stops every worker from taking its jobs."
            :queues="consumers"
            pausedKey="paused"
            showCounts
            canObliterate
            :busy="busy"
            @pause="(queue) => confirmToggle('consumers', 'pause', queue.name)"
            @resume="
              (queue) => confirmToggle('consumers', 'resume', queue.name)
            "
            @pause-all="confirmToggle('consumers', 'pause')"
            @resume-all="confirmToggle('consumers', 'resume')"
            @obliterate="(queue) => $refs.obliterateModal.open(queue.name)"
          />
          <QueuesTable
            title="Producers"
            description="Queues the Alfred App produces to. Pausing one stops this app from adding jobs to it."
            :queues="producers"
            pausedKey="producerPaused"
            :busy="busy"
            @pause="(queue) => confirmToggle('producers', 'pause', queue.name)"
            @resume="
              (queue) => confirmToggle('producers', 'resume', queue.name)
            "
            @pause-all="confirmToggle('producers', 'pause')"
            @resume-all="confirmToggle('producers', 'resume')"
          />
        </template>
      </div>

      <ObliterateQueueModal ref="obliterateModal" :onSubmit="obliterate" />
    </template>
  </Container>
</template>

<script>
import { ref, computed } from "vue";
import Container from "@/components/common/Container";
import QueuesTable from "@/components/admin/queues-page/QueuesTable";
import ObliterateQueueModal from "@/components/admin/queues-page/ObliterateQueueModal";
import formMixin from "@/mixins/formMixin";
import useConfirmationModal from "@/api/confirmationModal";
import eventBus from "@/utils/eventBus";

export default {
  name: "QueueManagement",
  mixins: [formMixin],
  components: { Container, QueuesTable, ObliterateQueueModal },
  setup() {
    // A queue Alfred both consumes and produces to shows in both tables.
    const queues = ref([]);
    const consumers = computed(() =>
      queues.value.filter((queue) => queue.owned)
    );
    const producers = computed(() =>
      queues.value.filter((queue) => queue.produced)
    );

    return {
      queues,
      consumers,
      producers,
      loading: ref(true),
      loadError: ref(false),
      busy: ref(false),
    };
  },
  async mounted() {
    await this.reload();
  },
  methods: {
    async reload() {
      this.loading = true;
      await this.fetchQueues();
      this.loading = false;
    },

    async fetchQueues() {
      const response = await this.handleSubmissionErrors(
        () => this.$alfredService.adminQueues(),
        {}
      );
      // handleSubmissionErrors returns undefined on failure. Don't show empty
      // tables then: that would read as "no queues registered".
      this.loadError = response === undefined;
      this.queues = response?.queues || [];
    },

    async confirmToggle(role, action, name = null) {
      const target = name || `all ${role}`;
      const verb =
        role === "producers" ? `${action} producing to` : `${action}`;
      const modal = useConfirmationModal();

      await modal.openModal({
        title: `${action === "pause" ? "Pause" : "Resume"} queue`,
        message: `Are you sure you want to ${verb} ${target}?`,
        onSubmit: () => this.toggle(role, action, name, target),
      });
    },

    async toggle(role, action, name, target) {
      const service = this.$alfredService;
      const calls = {
        consumers: {
          pause: () => service.adminPauseQueue(name),
          resume: () => service.adminResumeQueue(name),
        },
        producers: {
          pause: () => service.adminPauseProducing(name),
          resume: () => service.adminResumeProducing(name),
        },
      };

      await this.runAction(
        calls[role][action],
        `${action === "pause" ? "Paused" : "Resumed"} ${target}`
      );
    },

    async obliterate(name) {
      await this.runAction(
        () => this.$alfredService.adminObliterateQueue(name),
        `Obliterated ${name}`
      );
    },

    async runAction(apiCall, successText) {
      this.busy = true;
      try {
        const response = await this.handleSubmissionErrors(apiCall, {});
        // handleSubmissionErrors returns undefined on failure; a 204 comes
        // back as "", so don't test for truthiness.
        if (response !== undefined) {
          const failed = this.failedQueues(response);
          eventBus.trigger("notify", {
            notification: {
              group: "top-right",
              type: failed.length ? "error" : "success",
              text: failed.length
                ? `${successText}, except: ${failed.join(", ")}`
                : successText,
            },
          });
        }
        // Refetch either way: a failed bulk action may still have applied to
        // some of the queues.
        await this.fetchQueues();
      } finally {
        this.busy = false;
      }
    },

    // Bulk actions answer 200 with { results: { queue: "ok" | error code } }.
    failedQueues(response) {
      const results = (response && response.results) || {};
      return Object.entries(results)
        .filter(([, result]) => result !== "ok")
        .map(([queue, result]) => `${queue} (${result})`);
    },
  },
};
</script>
